import ImageCompressor from 'image-compressor.js';
import { getBucketAndKey, getOriginFromSignedUrl } from '../AWS';
import uuidv4 from "uuid/v4";

export const DEFAULT_IMAGE_MIME_TYPE = 'image/jpeg';
export const WEBP_IMAGE_MIME_TYPE = 'image/webp';
export const WEBP_IMAGE_EXTENSION = 'webp';
export const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', WEBP_IMAGE_MIME_TYPE];
export const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', WEBP_IMAGE_EXTENSION];
export const IMAGE_ACCEPT = [
  ...IMAGE_MIME_TYPES,
  ...IMAGE_EXTENSIONS.map(extension => `.${extension}`)
].join(',');
export const SLIDER_IMAGE_MIME_TYPES = [...IMAGE_MIME_TYPES, 'image/gif'];
export const SLIDER_IMAGE_EXTENSIONS = [...IMAGE_EXTENSIONS, 'gif'];
export const SLIDER_IMAGE_ACCEPT = [
  ...SLIDER_IMAGE_MIME_TYPES,
  ...SLIDER_IMAGE_EXTENSIONS.map(extension => `.${extension}`)
].join(',');
export const IMAGE_UPLOAD_ERROR_MESSAGE = 'Only JPG, JPEG, PNG, and WEBP image files can be uploaded.';
export const SLIDER_IMAGE_UPLOAD_ERROR_MESSAGE = 'Only JPG, JPEG, PNG, GIF, and WEBP image files can be uploaded.';

const normalizeFiles = files => Array.from(files || []);

export const isSupportedImageFile = (
  file,
  supportedMimeTypes = IMAGE_MIME_TYPES,
  supportedExtensions = IMAGE_EXTENSIONS
) => {
  if (!file) return false;

  const type = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();

  if (supportedMimeTypes.includes(type)) return true;
  return supportedExtensions.some(extension => name.endsWith(`.${extension}`));
};

export const hasUnsupportedImageFiles = (
  files,
  rejectedFiles = [],
  supportedMimeTypes = IMAGE_MIME_TYPES,
  supportedExtensions = IMAGE_EXTENSIONS
) => (
  [...normalizeFiles(files), ...normalizeFiles(rejectedFiles)]
    .some(file => !isSupportedImageFile(file, supportedMimeTypes, supportedExtensions))
);

export const hasUnsupportedSliderImageFiles = (files, rejectedFiles = []) => (
  hasUnsupportedImageFiles(
    files,
    rejectedFiles,
    SLIDER_IMAGE_MIME_TYPES,
    SLIDER_IMAGE_EXTENSIONS
  )
);

export const getImageExtension = (file, fallback = 'jpeg') => {
  if (!file) return fallback;

  const type = (file.type || '').toLowerCase();
  if (type === WEBP_IMAGE_MIME_TYPE) return WEBP_IMAGE_EXTENSION;
  if (type === 'image/png') return 'png';
  if (type === 'image/gif') return 'gif';
  if (type === 'image/jpeg') return 'jpeg';

  const name = file.name || '';
  if (name.includes('.')) return name.split('.').pop().toLowerCase();

  return fallback;
};

export const getImageMimeType = (file, fallback = DEFAULT_IMAGE_MIME_TYPE) => {
  if (!file) return fallback;

  const type = (file.type || '').toLowerCase();
  if (type.startsWith('image/')) return type;

  const extension = getImageExtension(file, '');
  if (extension === WEBP_IMAGE_EXTENSION) return WEBP_IMAGE_MIME_TYPE;
  if (extension === 'png') return 'image/png';
  if (extension === 'gif') return 'image/gif';
  if (extension === 'jpg' || extension === 'jpeg') return DEFAULT_IMAGE_MIME_TYPE;

  return fallback;
};

export const getCropImageMimeType = file => {
  const mimeType = getImageMimeType(file);
  return mimeType === 'image/gif' ? DEFAULT_IMAGE_MIME_TYPE : mimeType;
};

export const createImageFileFromBlob = (
  blob,
  fileName,
  sourceFile,
  fallbackMimeType = DEFAULT_IMAGE_MIME_TYPE
) => {
  const sourceMimeType = getImageMimeType(sourceFile, fallbackMimeType);
  const mimeType = getImageMimeType(blob, sourceMimeType);
  const extension = getImageExtension(
    { type: mimeType },
    getImageExtension(sourceFile)
  );

  return new File([blob], `${fileName}.${extension}`, { type: mimeType });
};

// Images Compressor function
export const ImagesCompressor = (file, options = {}) => {
  const imageCompressing = new ImageCompressor();
  const maxSize = 1000000; // byte
  const originSize = file.size; // origin size
  let isQuality = maxSize < originSize ? 0.6 : 1;
  // console.log('KUALITAS ', isQuality.toFixed(1));

  return imageCompressing.compress(file, {
    quality: isQuality,
    ...options
  })
  .then(result => result)
  .catch(error => file)
}

// Get Image Caching URL function
export const getCacheImage = (url, width = 500, options = {}) => {
  if(!url || url === "" || typeof url !== "string")
    return url;
  const origin = getOriginFromSignedUrl(url);
  const data = getBucketAndKey(origin);
  if(data && data.length > 0 && !data.includes('ka-faq-assets')){
    const [bucket, key] = data;
    const resize = {
      width
    };
    if(options.withoutEnlargement){
      resize.withoutEnlargement = true;
    }
    const cloudImageUrl = JSON.stringify({
      bucket,
      key: decodeURIComponent(key),
      edits: {
        resize
      }
    });
    const splittedKey = key.split('.');
    const fileType = splittedKey[splittedKey.length - 1];
    if(fileType === "gif"){
      return url;
    }
    const newImagePath = Buffer.from(cloudImageUrl).toString('base64');
    return "https://d5ibtax54de3q.cloudfront.net/" + newImagePath;
  }
  return url
    .replace(/kickavenue-user-uploads\.s3([.-][^.]+)?\.amazonaws\.com/, "d1i34sodava61.cloudfront.net")
    .replace(/kickavenue-assets\.s3([.-][^.]+)?\.amazonaws\.com/, "d3jneea6mhvdzo.cloudfront.net")
    .replace(/ka-faq-assets([.-][^.]+)?\.amazonaws\.com/, "d2wvligaol3ruc.cloudfront.net")
}


export const readFile = file => {
  return new Promise(resolve => {
    const reader = new FileReader()
    reader.addEventListener('load', () => resolve(reader.result), false)
    reader.readAsDataURL(file)
  })
}

//files Array | File
//multiples boolean
//payloads Object examples: {name: "folder/123", bucket_type: "assets"}
export const createUploadFileForm = (files, multiples, payloads = null) => {
  const tempPayload = new FormData();
  if(payloads)
    Object.keys(payloads).map(k => tempPayload.append(k, payloads[k]))
  if(multiples){
    files.map(file => tempPayload.append('file[]', file))
  } else{
    tempPayload.append('file', files)
  }
  return tempPayload;
}

export const addImageRawObject = file => {
  return {
    identifier: uuidv4(),
    fileInputOrigin: file, //for rollback.
    fileInput: file,
    URL: file.preview,
    status: false,
    isUploading: false,
    compressed: false
  }
}
